import { useState, Fragment } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { apiClient } from '@services/api'

type PaymentMode = 'Cash' | 'Net Banking' | 'UPI' | 'Cheque'

interface Payment {
  editedBy?: string | null
  updatedAt?: string
  id: number
  paymentNo: string
  amount: number
  date: string
  mode: PaymentMode
  referenceNo: string
  recordedBy: string
}

interface BillLine {
  gstRate?: number
  product: string
  route: 'Repacking' | 'Direct Sale'
  unit?: string
  quantity: number
  rate: number
  amount: number
  stock?:
    | { kind: 'raw'; batchNo: string; receivedKg: number; availableKg: number; status: string }
    | { kind: 'finished'; batchNo: string; packets: number; onHand: number; expiryDate: string }
    | null
}

interface BillDetail {
  id: number
  billNo: string
  supplierId: number
  supplierName: string
  billDate: string
  linkedPOId: number | null
  lineItems: BillLine[]
  itemCount: number
  taxableAmount: number
  gstRate: number
  gstAmount: number
  gstIncluded?: boolean
  taxInclusive?: boolean
  subTotal?: number
  discountMode?: 'percent' | 'amount'
  discountValue?: number
  discountAmount?: number
  taxBreakup?: Array<{ rate: number; taxable: number; gst: number; cgst: number; sgst: number }>
  roundOff: number
  amount: number
  amountPaid: number
  balance: number
  paymentStatus: 'Paid' | 'Partial' | 'Unpaid' | 'Draft'
  status: 'Active' | 'Draft' | 'Completed'
  createdAt: string
  createdBy: string
  supplier: { id: number; name: string; phone?: string; location?: string; address?: string; gstin?: string } | null
  purchaseOrder: { id: number; poNo: string } | null
  payments: Payment[]
  editable?: boolean
  editBlockedReason?: string | null
}

const today = () => new Date().toISOString().split('T')[0]

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const unitFor = (route: BillLine['route']) => (route === 'Repacking' ? 'kg' : 'pkt')
const money2 = (n: number) => `₹${(Math.round(n * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const discountLabel = (b: BillDetail) => (b.discountMode === 'percent' && b.discountValue ? `Discount (${b.discountValue}%)` : 'Discount')

const paymentBadge: Record<BillDetail['paymentStatus'], string> = {
  Paid: 'badge-full',
  Partial: 'badge-partial',
  Unpaid: 'badge-unpaid',
  Draft: 'badge-draft',
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-[9px] border-b border-[#EEEDE7] last:border-b-0 text-[13px]">
      <div className="text-ink-soft">{label}</div>
      <div className="text-right font-medium">{children}</div>
    </div>
  )
}

export function PurchaseBillDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['purchase-bill', id],
    queryFn: () => apiClient.get<any>(`/purchase-bills/${id}`),
    retry: false,
  })
  const bill: BillDetail | undefined = data?.data

  const [showPay, setShowPay] = useState(false)
  const [payAmount, setPayAmount] = useState('')
  const [payDate, setPayDate] = useState(today())
  const [payMode, setPayMode] = useState<PaymentMode>('Cash')
  const [payRef, setPayRef] = useState('')
  const [payError, setPayError] = useState('')
  const [paying, setPaying] = useState(false)
  const location = useLocation()
  const [paySuccess, setPaySuccess] = useState<string>(() => (location.state as any)?.message || '') // e.g. "updated" after an edit
  const [editingGst, setEditingGst] = useState(false)
  const [gstDraft, setGstDraft] = useState(0)
  const [roundDraft, setRoundDraft] = useState('')
  const [gstSaving, setGstSaving] = useState(false)
  const [gstError, setGstError] = useState('')
  const GST_RATES = [0, 5, 12, 18, 28]

  const [draftBusy, setDraftBusy] = useState(false)

  // Correcting a recorded payment (part payments, wrong amount / mode / reference). The payment number is kept.
  const [editPay, setEditPay] = useState<{ id: number; paymentNo: string; amount: string; date: string; mode: PaymentMode; referenceNo: string } | null>(null)
  const [editPayError, setEditPayError] = useState('')
  const [editPayBusy, setEditPayBusy] = useState(false)
  const refreshAfterPayment = async (message: string) => {
    await refetch()
    for (const k of ['purchase-bills', 'recent-purchase-bills', 'dashboard-home', 'outstanding-bills', 'transactions', 'ledger']) queryClient.invalidateQueries({ queryKey: [k] })
    setPaySuccess(message)
    setTimeout(() => setPaySuccess(''), 5000)
  }
  const savePaymentEdit = async () => {
    if (!bill || !editPay) return
    const amount = Number(editPay.amount)
    setEditPayError('')
    if (!(amount > 0)) return setEditPayError('Enter an amount greater than 0')
    if (!editPay.date) return setEditPayError('Payment date is required')
    if (editPay.mode === 'Cheque' && !editPay.referenceNo.trim()) return setEditPayError('Cheque number is required for cheque payments')
    setEditPayBusy(true)
    try {
      const res = await apiClient.patch<any>(`/purchase-bills/${bill.id}/payments/${editPay.id}`, { amount, date: editPay.date, mode: editPay.mode, referenceNo: editPay.referenceNo.trim() })
      setEditPay(null)
      await refreshAfterPayment(res?.message || 'Payment updated')
    } catch (error: any) {
      setEditPayError(error.response?.data?.message || 'Could not update the payment')
    } finally {
      setEditPayBusy(false)
    }
  }
  const deletePayment = async (p: Payment) => {
    if (!bill || !window.confirm(`Remove ${p.paymentNo} (${formatINR(p.amount)} on ${formatDate(p.date)})? The balance payable goes back up by that amount.`)) return
    setEditPayBusy(true)
    try {
      const res = await apiClient.delete<any>(`/purchase-bills/${bill.id}/payments/${p.id}`)
      await refreshAfterPayment(res?.message || 'Payment removed')
    } catch (error: any) {
      setPayError(error.response?.data?.message || 'Could not remove the payment')
    } finally {
      setEditPayBusy(false)
    }
  }
  const submitDraft = async () => {
    if (!bill || !window.confirm(`Submit ${bill.billNo} to stock? Repacking lines create bulk batches and direct-sale lines go straight into Stock for Sale.`)) return
    setDraftBusy(true); setGstError('')
    try {
      const res = await apiClient.post<any>(`/purchase-bills/${bill.id}/submit`, {})
      await refetch()
      for (const k of ['purchase-bills', 'recent-purchase-bills', 'dashboard-home', 'stock-overview', 'repack-pending', 'billing-items', 'outstanding-bills']) queryClient.invalidateQueries({ queryKey: [k] })
      setPaySuccess(res?.message || 'Bill submitted')
    } catch (error: any) { setGstError(error.response?.data?.message || 'Could not submit the bill') } finally { setDraftBusy(false) }
  }
  const deleteDraft = async () => {
    if (!bill || !window.confirm(`Delete draft ${bill.billNo}? This cannot be undone.`)) return
    setDraftBusy(true)
    try {
      await apiClient.delete<any>(`/purchase-bills/${bill.id}`)
      queryClient.invalidateQueries({ queryKey: ['purchase-bills'] }); queryClient.invalidateQueries({ queryKey: ['recent-purchase-bills'] })
      navigate('/dashboard/procurement/purchase-bills')
    } catch (error: any) { setGstError(error.response?.data?.message || 'Could not delete the draft'); setDraftBusy(false) }
  }

  const saveGst = async () => {
    if (!bill) return
    setGstSaving(true)
    setGstError('')
    try {
      const res = await apiClient.patch<any>(`/purchase-bills/${bill.id}/gst`, { gstRate: gstDraft, gstIncluded: gstDraft > 0, roundOff: roundDraft.trim() === '' ? 0 : Number(roundDraft) })
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['purchase-bills'] })
      queryClient.invalidateQueries({ queryKey: ['recent-purchase-bills'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-home'] })
      setEditingGst(false)
      setPaySuccess(res?.message || 'GST updated')
      setTimeout(() => setPaySuccess(''), 4000)
    } catch (error: any) {
      setGstError(error.response?.data?.message || 'Failed to update GST')
    } finally {
      setGstSaving(false)
    }
  }

  const openPay = () => {
    setPayAmount(String(bill?.balance ?? ''))
    setPayDate(today())
    setPayMode('Cash')
    setPayRef('')
    setPayError('')
    setShowPay(true)
  }

  const submitPayment = async () => {
    if (!bill) return
    const amount = Number(payAmount)
    setPayError('')
    if (!(amount > 0)) return setPayError('Enter a payment amount greater than 0')
    if (amount > bill.balance) return setPayError(`Amount exceeds the balance payable (${formatINR(bill.balance)})`)
    if (!payDate) return setPayError('Payment date is required')
    if (payMode === 'Cheque' && !payRef.trim()) return setPayError('Cheque number is required for cheque payments')
    setPaying(true)
    try {
      const res = await apiClient.post<any>(`/purchase-bills/${bill.id}/payments`, {
        amount,
        date: payDate,
        mode: payMode,
        referenceNo: payRef.trim(),
      })
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['purchase-bills'] })
      queryClient.invalidateQueries({ queryKey: ['recent-purchase-bills'] })
      setShowPay(false)
      setPaySuccess(res?.message || 'Payment recorded')
      setTimeout(() => setPaySuccess(''), 4000)
    } catch (error: any) {
      setPayError(error.response?.data?.message || 'Failed to record payment')
    } finally {
      setPaying(false)
    }
  }

  const payPreview = () => {
    if (!bill) return null
    const amount = Number(payAmount)
    if (!(amount > 0)) return null
    if (amount > bill.balance) return { cls: 'hint-error', text: `→ ${formatINR(amount)} exceeds the balance payable of ${formatINR(bill.balance)}.` }
    const after = bill.balance - amount
    return after <= 0
      ? { cls: 'hint calc', text: `→ This will fully settle ${bill.billNo} — status will move from ${bill.paymentStatus} to Paid.` }
      : { cls: 'hint calc', text: `→ Balance after this payment: ${formatINR(after)} — status will be Partial.` }
  }

  if (isLoading) {
    return (
      <div className="card text-center py-8 text-ink-soft text-[13px]">Loading bill…</div>
    )
  }

  if (isError || !bill) {
    return (
      <div>
        <div className="hdr">
          <h1>Purchase bill not found</h1>
          <p>The bill you're looking for doesn't exist or was removed.</p>
        </div>
        <Link to="/dashboard/procurement/purchase-bills/list" className="btn-ghost">
          ← All bills
        </Link>
      </div>
    )
  }

  const repackingLines = bill.lineItems.filter((l) => l.route === 'Repacking')
  const directLines = bill.lineItems.filter((l) => l.route === 'Direct Sale')

  return (
    <div>
      <div className="hdr flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1>
            Bill {bill.billNo} — {bill.supplierName}
          </h1>
          <p>
            Recorded {formatDate(bill.billDate)} by {bill.createdBy} · {bill.itemCount} items · {formatINR(bill.taxableAmount)}{bill.gstRate > 0 ? ` + GST ${bill.gstRate}%` : ' (non-GST bill)'} = {formatINR(bill.amount)}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap justify-end">
          <Link to="/dashboard/procurement/purchase-bills/list" className="btn-ghost btn-sm">
            ← All bills
          </Link>
          {bill.editable !== false ? (
            <button type="button" className="btn-ghost btn-sm" onClick={() => navigate(`/dashboard/procurement/purchase-bills?edit=${bill.id}`)} data-testid="edit-bill" title="Change the supplier, bill number, date, GST or items — stock is re-posted from the new lines">
              Edit
            </button>
          ) : (
            <span className="text-[11.5px] text-ink-soft" title={bill.editBlockedReason || ''}>Locked — {bill.editBlockedReason}</span>
          )}
          <Link to="/dashboard/procurement/purchase-bills" className="btn-ghost btn-sm">
            + New Purchase Bill
          </Link>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Bill Details</h2>
          <DetailRow label="Supplier">
            <div>{bill.supplierName}</div>
            {(bill.supplier?.phone || bill.supplier?.location) && (
              <div className="text-ink-soft font-normal text-[11.5px]">
                {[bill.supplier?.location, bill.supplier?.phone].filter(Boolean).join(' · ')}
              </div>
            )}
            {bill.supplier?.address && <div className="text-ink-soft font-normal text-[11.5px] whitespace-pre-line">{bill.supplier.address}</div>}
            {bill.supplier?.gstin && <div className="text-ink-soft font-normal text-[11.5px]">GSTIN {bill.supplier.gstin}</div>}
          </DetailRow>
          <DetailRow label="Supplier Bill No.">{bill.billNo}</DetailRow>
          <DetailRow label="Bill Date">{formatDate(bill.billDate)}</DetailRow>
          <DetailRow label="Linked Purchase Order">
            {bill.purchaseOrder ? bill.purchaseOrder.poNo.split(' — ')[0] : <span className="text-ink-soft font-normal">— No PO, ad-hoc bill —</span>}
          </DetailRow>
          <DetailRow label="Bill Status">
            <span className={`badge ${bill.status === 'Draft' ? 'badge-draft' : 'badge-ok'}`}>{bill.status}</span>
          </DetailRow>
          <DetailRow label="Entered by">{bill.createdBy}</DetailRow>
        </div>

        <div className="card">
          <h2>Payment Position</h2>
          {paySuccess && <div className="alert alert-success">{paySuccess}</div>}
          {(bill.discountAmount || 0) > 0 && <DetailRow label="Sub Total (items)">{money2(bill.subTotal ?? bill.taxableAmount + (bill.discountAmount || 0))}</DetailRow>}
          {(bill.discountAmount || 0) > 0 && <DetailRow label={discountLabel(bill)}>- {money2(bill.discountAmount || 0)}</DetailRow>}
          {bill.taxInclusive && <DetailRow label="Sub Total (incl. GST)">{money2(bill.subTotal ?? bill.taxableAmount + bill.gstAmount + (bill.discountAmount || 0))}</DetailRow>}
          <DetailRow label="Taxable Value (before GST)">{money2(bill.taxableAmount)}</DetailRow>
          <DetailRow label={((bill.taxBreakup || []).filter((t) => t.rate > 0).length > 1 ? 'GST (mixed rates — see items)' : bill.gstRate > 0 ? `GST @ ${bill.gstRate}% (CGST ${bill.gstRate / 2}% + SGST ${bill.gstRate / 2}%)` : 'GST — non-GST bill') + (bill.taxInclusive && bill.gstAmount > 0 ? ' — included in rates' : '')}>
            {editingGst ? (
              <span className="inline-flex items-center gap-2 flex-wrap justify-end" data-testid="gst-editor">
                <select value={gstDraft} onChange={(e) => setGstDraft(Number(e.target.value))} className="!w-40 !py-[5px] !text-xs">
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r === 0 ? 'No GST (estimate / non-GST)' : `Set every item to ${r}%`}
                    </option>
                  ))}
                </select>
                <input type="number" step="0.01" min={-100} max={100} value={roundDraft} onChange={(e) => setRoundDraft(e.target.value)} placeholder="round off" className="!w-24 !py-[5px] !text-xs" title="Round off / discount on the bill" />
                <button type="button" className="btn-accent btn-xs" disabled={gstSaving} onClick={saveGst}>
                  {gstSaving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn-ghost btn-xs" disabled={gstSaving} onClick={() => setEditingGst(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                {bill.gstAmount > 0 ? money2(bill.gstAmount) : '—'}
                <button
                  type="button"
                  className="text-[11px] text-primary-500 hover:underline font-normal"
                  onClick={() => {
                    setGstDraft(bill.gstRate)
                    setRoundDraft(bill.roundOff ? String(bill.roundOff) : '')
                    setGstError('')
                    setEditingGst(true)
                  }}
                  data-testid="change-gst"
                >
                  Change
                </button>
              </span>
            )}
            {gstError && <div className="hint-error text-right">{gstError}</div>}
          </DetailRow>
          {(bill.roundOff || 0) !== 0 && <DetailRow label="Round Off / Discount">{bill.roundOff < 0 ? '-' : '+'}{formatINR(Math.abs(bill.roundOff))}</DetailRow>}
          <DetailRow label="Grand Total">
            <span className="font-bold">{formatINR(bill.amount)}</span>
          </DetailRow>
          <DetailRow label="Paid so far">{formatINR(bill.amountPaid)}</DetailRow>
          <DetailRow label="Balance Payable">
            <span className={`text-[15px] font-bold ${bill.balance > 0 ? 'text-danger' : 'text-good'}`}>{formatINR(bill.balance)}</span>
          </DetailRow>
          <DetailRow label="Payment Status">
            <span className={`badge ${paymentBadge[bill.paymentStatus]}`}>{bill.paymentStatus}</span>
          </DetailRow>

          {bill.status === 'Draft' && (
            <div className="rounded-[6px] p-[12px] mt-3" style={{ background: 'var(--accent-soft)' }}>
              <div className="text-[12.5px] mb-2"><b>Draft bill — nothing is in stock yet.</b> Check the items and GST above, then submit to add the goods to stock.</div>
              <div className="flex gap-2 flex-wrap">
                <button type="button" className="btn-accent" disabled={draftBusy} onClick={submitDraft} data-testid="submit-draft">{draftBusy ? 'Submitting…' : 'Submit to stock'}</button>
                <button type="button" className="btn-danger btn-sm" disabled={draftBusy} onClick={deleteDraft}>Delete draft</button>
              </div>
            </div>
          )}
          {bill.balance > 0 && bill.status !== 'Draft' && !showPay && (
            <div className="btnrow">
              <button type="button" className="btn-accent" onClick={openPay}>
                Record Payment
              </button>
            </div>
          )}

          {showPay && (
            <div className="bg-primary-soft rounded-[6px] p-[14px] mt-[14px]" data-testid="payment-panel">
              <div className="row2">
                <div>
                  <label>Amount</label>
                  <input type="number" min={0} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                </div>
                <div>
                  <label>Date</label>
                  <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                </div>
              </div>
              <div className="row2 mt-3">
                <div>
                  <label>Mode</label>
                  <select value={payMode} onChange={(e) => setPayMode(e.target.value as PaymentMode)}>
                    <option>Cash</option>
                    <option>Net Banking</option>
                    <option>UPI</option>
                    <option>Cheque</option>
                  </select>
                </div>
                <div>
                  <label>
                    Reference No. <small>— cheque no. / UTR{payMode === 'Cheque' ? '' : ', optional'}</small>
                  </label>
                  <input type="text" value={payRef} onChange={(e) => setPayRef(e.target.value)} />
                </div>
              </div>
              {payPreview() && <div className={payPreview()!.cls}>{payPreview()!.text}</div>}
              {payError && <div className="hint-error">{payError}</div>}
              <div className="flex gap-2 mt-3">
                <button type="button" className="btn-accent btn-sm" disabled={paying} onClick={submitPayment}>
                  {paying ? 'Recording…' : 'Record Payment'}
                </button>
                <button type="button" className="btn-ghost btn-sm" disabled={paying} onClick={() => setShowPay(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {bill.payments.length > 0 && (
            <>
              <div className="field-label-row mt-[14px]">
                <h2 style={{ marginBottom: 0 }}>Payment history <small>— {bill.payments.length} payment{bill.payments.length === 1 ? '' : 's'}, {formatINR(bill.amountPaid)} paid</small></h2>
              </div>
              {editPayError && <div className="hint-error">{editPayError}</div>}
              <table className="mt-[6px]" data-testid="payment-history">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Payment</th>
                    <th>Mode</th>
                    <th>Ref</th>
                    <th className="num">Amount</th>
                    <th>Recorded by</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...bill.payments].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id - b.id)).map((p) =>
                    editPay && editPay.id === p.id ? (
                      <tr key={p.id} className="bg-[#FFFBEB]" data-testid="payment-edit-row">
                        <td><input type="date" value={editPay.date} onChange={(e) => setEditPay({ ...editPay, date: e.target.value })} className="!w-36 !py-[5px] !text-xs !mb-0" /></td>
                        <td>{p.paymentNo}</td>
                        <td>
                          <select value={editPay.mode} onChange={(e) => setEditPay({ ...editPay, mode: e.target.value as PaymentMode })} className="!w-32 !py-[5px] !text-xs !mb-0">
                            {(['Cash', 'Net Banking', 'UPI', 'Cheque'] as PaymentMode[]).map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </td>
                        <td><input type="text" value={editPay.referenceNo} placeholder={editPay.mode === 'Cheque' ? 'cheque no.' : 'UTR / ref'} onChange={(e) => setEditPay({ ...editPay, referenceNo: e.target.value })} className="!w-32 !py-[5px] !text-xs !mb-0" /></td>
                        <td className="num"><input type="number" step="0.01" min={0} value={editPay.amount} onChange={(e) => setEditPay({ ...editPay, amount: e.target.value })} className="!w-28 !py-[5px] !text-xs !mb-0 text-right" /></td>
                        <td className="text-ink-soft text-[11.5px]">{p.recordedBy}</td>
                        <td className="whitespace-nowrap text-right">
                          <button type="button" className="btn-accent btn-xs" disabled={editPayBusy} onClick={savePaymentEdit}>{editPayBusy ? 'Saving…' : 'Save'}</button>
                          <button type="button" className="btn-ghost btn-xs ml-1" disabled={editPayBusy} onClick={() => { setEditPay(null); setEditPayError('') }}>Cancel</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={p.id}>
                        <td className="whitespace-nowrap">{formatDate(p.date)}</td>
                        <td>{p.paymentNo}</td>
                        <td>{p.mode}</td>
                        <td className="text-ink-soft">{p.referenceNo || '—'}</td>
                        <td className="num font-semibold">{formatINR(p.amount)}</td>
                        <td className="text-ink-soft text-[11.5px]">{p.recordedBy}{p.editedBy ? <div>edited by {p.editedBy}</div> : null}</td>
                        <td className="whitespace-nowrap text-right">
                          <button type="button" className="btn-ghost btn-xs" disabled={editPayBusy} onClick={() => { setEditPayError(''); setEditPay({ id: p.id, paymentNo: p.paymentNo, amount: String(p.amount), date: p.date, mode: p.mode, referenceNo: p.referenceNo || '' }) }} title="Correct the amount, date, mode or reference">Edit</button>
                          <button type="button" className="btn-ghost btn-xs ml-1" style={{ color: '#b42318' }} disabled={editPayBusy} onClick={() => deletePayment(p)} title="Remove this payment — the balance goes back up">Remove</button>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </>
          )}
          <div className="hint">Payments recorded here post to the Purchase Ledger and the supplier's payable balance immediately.</div>
        </div>
      </div>

      <div className="card mt-[18px]">
        <h2>
          Items Purchased <small>— {bill.itemCount} lines · {repackingLines.length} Repacking, {directLines.length} Direct Sale</small>
        </h2>
        <table>
          <thead>
            <tr>
              <th className="w-8">#</th>
              <th>Product</th>
              <th>Route</th>
              <th>Stock</th>
              <th className="num">Qty</th>
              <th className="num">Rate</th>
              {bill.gstIncluded !== false && bill.gstAmount > 0 && <th className="num">GST</th>}
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lineItems.map((l, i) => (
              <tr key={`${l.product}-${i}`}>
                <td className="text-ink-soft">{i + 1}</td>
                <td>{l.product}</td>
                <td>
                  <span className={`badge ${l.route === 'Repacking' ? 'badge-pending' : 'badge-ok'}`}>{l.route}</span>
                </td>
                <td className="text-[11.5px]">
                  {!l.stock ? (
                    <span className="text-ink-soft">{bill.status === 'Draft' ? 'Not in stock yet (draft)' : '—'}</span>
                  ) : l.stock.kind === 'raw' ? (
                    <>
                      <div>
                        {l.stock.batchNo} · {l.stock.availableKg} of {l.stock.receivedKg} kg left
                      </div>
                      <div className="text-ink-soft">
                        {l.stock.status}
                        {l.stock.availableKg > 0 && (
                          <>
                            {' · '}
                            <Link to={`/dashboard/repacking?product=${encodeURIComponent(l.product)}`} className="text-primary-500 hover:underline">
                              Repack →
                            </Link>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        {l.stock.batchNo} · {l.stock.packets.toLocaleString('en-IN')} of {l.quantity.toLocaleString('en-IN')} {l.unit || 'pkt'} left
                      </div>
                      <div className="text-ink-soft">
                        {l.stock.onHand.toLocaleString('en-IN')} on hand ·{' '}
                        <Link to="/dashboard/stock" className="text-primary-500 hover:underline">
                          Stock →
                        </Link>
                      </div>
                    </>
                  )}
                </td>
                <td className="num">
                  {l.quantity.toLocaleString('en-IN')} {l.unit || unitFor(l.route)}
                </td>
                <td className="num">{money2(l.rate)}</td>
                {bill.gstIncluded !== false && bill.gstAmount > 0 && <td className="num">{l.gstRate ?? bill.gstRate}%</td>}
                <td className="num font-semibold">{money2(l.amount)}</td>
              </tr>
            ))}
            {(() => {
              const span = bill.gstIncluded !== false && bill.gstAmount > 0 ? 7 : 6
              const breakup = (bill.taxBreakup || []).filter((t) => t.rate > 0)
              const subTotal = bill.subTotal ?? bill.taxableAmount + (bill.discountAmount || 0)
              const row = (label: React.ReactNode, value: React.ReactNode, key: string, strong = false) => (
                <tr className="bg-[#FAFAF7]" key={key}>
                  <td colSpan={span} className={strong ? 'text-right font-semibold' : 'text-right'}>{label}</td>
                  <td className={strong ? 'num font-bold text-[14px]' : 'num'}>{value}</td>
                </tr>
              )
              return (
                <>
                  {row(bill.taxInclusive ? 'Sub Total (incl. GST)' : 'Sub Total', money2(subTotal), 'sub')}
                  {(bill.discountAmount || 0) > 0 && row(discountLabel(bill), '- ' + money2(bill.discountAmount || 0), 'disc')}
                  {((bill.discountAmount || 0) > 0 || bill.taxInclusive) && row(bill.taxInclusive ? 'Taxable Value (excl. GST)' : 'Taxable Value (after discount)', money2(bill.taxableAmount), 'taxable')}
                  {breakup.length === 0 && row(bill.gstIncluded === false || bill.gstAmount <= 0 ? 'GST — non-GST bill' : 'GST', bill.gstAmount > 0 ? money2(bill.gstAmount) : '—', 'gst0')}
                  {breakup.map((t) => (
                    <Fragment key={t.rate}>
                      {row(<>CGST {t.rate / 2}% <small className="text-ink-soft">on {money2(t.taxable)}</small></>, money2(t.cgst), 'c' + t.rate)}
                      {row(<>SGST {t.rate / 2}%</>, money2(t.sgst), 's' + t.rate)}
                    </Fragment>
                  ))}
                  {(breakup.length > 1 || (bill.taxInclusive && breakup.length > 0)) && row(bill.taxInclusive ? 'Total GST (included in the rates — not added)' : 'Total GST', money2(bill.gstAmount), 'gstsum')}
                  {(bill.roundOff || 0) !== 0 && row('Round Off', (bill.roundOff < 0 ? '- ' : '+ ') + money2(Math.abs(bill.roundOff)), 'round')}
                  {row('Grand Total', money2(bill.amount), 'grand', true)}
                </>
              )
            })()}
          </tbody>
        </table>
        <div className="hint">
          {bill.status === 'Draft'
            ? 'Draft bill — nothing has been added to stock yet.'
            : `${repackingLines.length} Repacking line${repackingLines.length === 1 ? '' : 's'} created bulk stock batches (in the repacking pipeline); ${directLines.length} Direct Sale line${directLines.length === 1 ? '' : 's'} went straight into Stock for Sale.`}
        </div>
      </div>
    </div>
  )
}
